import type { ReactNode } from "react";
import { InputSize, Select, SelectType, type SelectItem } from "./ring.ts";

export interface ValueOption {
  id: number;
  label: string;
  /** Leading visual: colour badge for option values, avatar for users. */
  icon?: ReactNode;
}

export interface ValueSelectProps {
  options: ValueOption[];
  selected: ReadonlySet<number>;
  multi: boolean;
  /** Full new selection: `[]` clears, single pickers give at most one id. */
  onChange: (ids: number[]) => void;
  /** Shown on the anchor when nothing is selected ("No type", "Required", "—"). */
  emptyLabel: string;
  /** Optional single fields get a reset row at the top of the list. */
  clearable?: boolean;
  clearLabel?: string;
  /** `inline`: YouTrack's field-panel look (link-blue text). Otherwise a select-shaped button for forms. */
  inline?: boolean;
  disabled?: boolean;
  className?: string;
}

const RESET_KEY = -1;

/**
 * Option-set / user value picker on Ring's Select — the same component YouTrack
 * uses: filter box focused on open, ticks, arrows, Esc, "Filter items".
 */
export function ValueSelect({ options, selected, multi, onChange, emptyLabel, clearable = false, clearLabel = "—", inline = false, disabled = false, className }: ValueSelectProps) {
  // Ring gives its own glyph/avatar column an 8px right margin (.ring-list-glyph); leftNodes get none, so add it here.
  const rows: SelectItem[] = options.map((o) => ({ key: o.id, label: o.label, leftNodes: o.icon === undefined ? undefined : <span className="mr-2 inline-flex">{o.icon}</span> }));
  const byId = new Map(rows.map((r) => [r.key as number, r]));
  const type = inline ? SelectType.INLINE : SelectType.BUTTON;
  // Inline (field panel): YouTrack shows the bare link-blue value, no chevron. Ring's inline anchor always draws one
  // (`hideArrow` only covers the button type), so .no-caret hides it in CSS.
  const common = { type, filter: true, disabled, label: emptyLabel, size: InputSize.FULL, ...(inline ? { buttonClassName: "no-caret" } : {}), ...(className ? { className } : {}) };
  if (multi) {
    const chosen = [...selected].map((id) => byId.get(id)).filter((r): r is SelectItem => r !== undefined);
    return (
      <Select
        {...common}
        multiple
        data={rows}
        selected={chosen}
        selectedLabel={chosen.length ? chosen.map((r) => String(r.label)).join(", ") : emptyLabel}
        onChange={(items: SelectItem[]) => onChange(items.map((i) => Number(i.key)))}
      />
    );
  }
  const data: SelectItem[] = clearable ? [{ key: RESET_KEY, label: clearLabel, isResetItem: true }, ...rows] : rows;
  const cur = [...selected].map((id) => byId.get(id)).find((r) => r !== undefined) ?? null;
  return (
    <Select
      {...common}
      multiple={false}
      data={data}
      selected={cur}
      onChange={(item: SelectItem | null) => onChange(item === null || item.key === RESET_KEY ? [] : [Number(item.key)])}
    />
  );
}
